import { FC, useMemo, useRef } from 'react'

import { TableRef } from '@revolut-internal/ui-kit'

import { LimitedTable } from 'components/LimitedTable/LimitedTable'
import { ADDITION_SUFFIX } from 'consts/chart'
import { useChartTableSelectedRows } from 'hooks/useChartTableSelectedRows'
import { useTableState } from 'hooks/useTableState'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import { ChartTableProps, TableDataItem } from 'types'
import { injectAdditionalDataToTableData } from 'utils/charts/injectAdditionalDataToTableData'
import { prepareTableColumnsFromChartData } from 'utils/charts/prepareTableColumnsFromChartData'
import { prepareTableDataFromChartData } from 'utils/charts/prepareTableDataFromChartData'

export const TicketsVolumeDetailsTable: FC<ChartTableProps> = ({
  data,
  additionalData,
  charts,
  selectedCharts,
  additionalDataLabel,
  entityName,
  onSelectCharts,
}) => {
  const tableRef = useRef<TableRef<TableDataItem>>(null)

  const { state: tableState, setSorting } = useTableState(
    UrlStateStorageKeys.TicketVolumeTableState,
  )

  const preparedData = useMemo(() => prepareTableDataFromChartData(data), [data])
  const preparedAdditionalData = useMemo(
    () =>
      additionalData
        ? prepareTableDataFromChartData(additionalData, key =>
            key.replace(ADDITION_SUFFIX, ''),
          )
        : [],
    [additionalData],
  )
  const preparedDataWithAdditional = useMemo(
    () =>
      injectAdditionalDataToTableData(
        preparedData,
        preparedAdditionalData,
        additionalDataLabel ?? 'Compare with',
      ),
    [preparedData, preparedAdditionalData, additionalDataLabel],
  )

  const { selectedRowIds, onSelect } = useChartTableSelectedRows({
    data: preparedDataWithAdditional,
    selectedCharts,
    charts,
    onSelectCharts,
  })
  const columns = useMemo(
    () =>
      prepareTableColumnsFromChartData({
        data,
        additionalData,
        selectedRowIds,
        firstColumnLabel: entityName,
        isExpandable: true,
        onRowSelect: onSelect,
      }),
    [data, additionalData, selectedRowIds, entityName, onSelect],
  )

  return (
    <LimitedTable
      ref={tableRef}
      state={tableState}
      columns={columns}
      data={preparedDataWithAdditional}
      rowsToShow={12}
      aria-label="Ticket volume table"
      primaryColumnId="name"
      resizable
      expandable
      enableMultiSort
      onSortingChange={setSorting}
    />
  )
}
